import { useContext, useEffect, useMemo, useState } from 'react'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'

import { useProductPageContext } from '../../contexts'
import { RecommendationContext } from '../../contexts/Recommendations/recommendations-context'
import { useBuyButton } from '../Sidebar/ProductAction/BuyButton/hooks/useBuyButton'
import type { DataRecommendation } from '../../types/linx-recommendations'
import type { Product } from '../../types/product'
import { trackCrossSellV2PairAddedToCart } from './trackCrossSellV2AddedToCart'

const useCrossSellV2 = () => {
  const { currentSku, product } = useProductPageContext()
  const { pageData } = useGlobalContext()
  const { addToCart } = useBuyButton({
    propCurrentSku: currentSku,
  })

  const { dyDataRecommendations, loading } = useContext(RecommendationContext)

  const [shouldSelectSku, setShouldSelectSku] = useState(false)
  const [currentProductSelectedSku, setCurrentProductSelectedSku] = useState<
    string | null
  >(null)

  const [suggestionProductSelectedSku, setSuggestionProductSelectedSku] =
    useState<string | null>(null)

  const currentProduct = useMemo(() => {
    return {
      ...product,
      items: product.items.filter(
        (item) => item.productId === currentSku.productId
      ),
    }
  }, [product, currentSku.productId])

  const frequentlyBoughtTogether = useMemo(() => {
    return dyDataRecommendations?.filter((item: DataRecommendation) =>
      item.showcases.some(
        (showcase) =>
          showcase.feature.toLowerCase().includes('compre junto') ||
          showcase.name.toLowerCase().includes('compre junto')
      )
    )
  }, [dyDataRecommendations])

  const showcase = frequentlyBoughtTogether?.[0]?.showcases?.[0]
  const { decisionId, id } = showcase ?? {}
  const suggestionProduct = showcase?.displays?.[0]
    ?.recommendations?.[0] as Product

  const suggestionSku = useMemo(() => {
    return suggestionProduct?.items?.find((item) => item.isAvailable)
  }, [suggestionProduct])

  useEffect(() => {
    if (currentSku?.itemId) {
      setCurrentProductSelectedSku(currentSku.itemId)
    }

    if (suggestionSku?.itemId) {
      setSuggestionProductSelectedSku(suggestionSku.itemId)
    } else {
      setSuggestionProductSelectedSku(null)
    }

    setShouldSelectSku(false)
  }, [currentSku.productId, currentSku.itemId, suggestionSku?.itemId])

  const prices = useMemo(() => {
    const currentPrice = currentSku.sellerDefault.sellerOffer.cashPrice ?? 0
    const currentList = currentSku.sellerDefault.sellerOffer.listPrice ?? 0

    const suggestionPrice =
      suggestionSku?.sellerDefault?.sellerOffer?.cashPrice ?? 0

    const suggestionList =
      suggestionSku?.sellerDefault?.sellerOffer?.listPrice ?? 0

    return {
      totalCashPrice: currentPrice + suggestionPrice,
      totalListPrice: currentList + suggestionList,
    }
  }, [currentSku, suggestionSku])

  const handleAddProducts = async () => {
    if (
      !suggestionProductSelectedSku ||
      !currentProductSelectedSku ||
      !suggestionProduct
    ) {
      setShouldSelectSku(true)

      return
    }

    const selectedCurrentData = currentProduct.items.find(
      (item) => item.itemId === currentProductSelectedSku
    )

    const selectedSuggestionData = suggestionProduct.items.find(
      (item) => item.itemId === suggestionProductSelectedSku
    )

    if (!selectedCurrentData || !selectedSuggestionData) return

    const { itemId: curItemId, sellerDefault: curSellerDefault } =
      selectedCurrentData

    const { itemId: suggItemId, sellerDefault: suggSellerDefault } =
      selectedSuggestionData

    const addedToCart = await addToCart({
      item: [
        {
          id: curItemId,
          productId: currentProduct.productId,
          seller: curSellerDefault.sellerId,
          quantity: 1,
          index: 0,
          price: curSellerDefault.sellerOffer.cashPrice,
        },
        {
          id: suggItemId,
          productId: suggestionProduct.productId,
          seller: suggSellerDefault.sellerId,
          quantity: 1,
          index: 1,
          price: suggSellerDefault.sellerOffer.cashPrice,
        },
      ],
      type: 'addToCart',
      shouldOpenFeedback: true,
      origin: 'add_together',
      skipAmplitudeEvent: true,
    })

    if (!addedToCart) {
      return
    }

    trackCrossSellV2PairAddedToCart({
      mainSku: selectedCurrentData,
      crossSellSku: selectedSuggestionData,
      pageData,
      eventData: { id, decisionId },
    })
  }

  return {
    currentProduct,
    currentSku,
    suggestionProduct,
    eventData: { id, decisionId },
    decisionId,
    totalListPrice: prices.totalListPrice,
    totalCashPrice: prices.totalCashPrice,
    currentProductSelectedSku,
    suggestionProductSelectedSku,
    shouldSelectSku,
    loading,
    handleAddProducts,
    setSuggestionProductSelectedSku,
    setCurrentProductSelectedSku,
  }
}

export default useCrossSellV2
